// Google Maps JS API loader (maps, marker, places) + Places Autocomplete helper.
//
// Extension points for future Google Maps usage:
//  - Directions API: `await getGoogleMapsLibrary('routes')` + DirectionsService
//    to replace curved bezier paths with real routes in rebuildMapRoutes.
//  - Places Nearby Search: `await getGoogleMapsLibrary('places')` already loaded;
//    use `Place.searchNearby()` for POI suggestions.
//  - Distance Matrix: `await getGoogleMapsLibrary('routes')` + DistanceMatrixService.

import { authFetch } from '../../core/auth.js';
import { textCacheKey, detailsCacheKey, geocodeCacheKey, geocodeQueryCacheKey, cacheGet, cacheSet } from './placesCache.js';
import { pickCityCenter } from './cityCenter.js';

let _bootstrapPromise = null;
let _config = null;

async function _fetchConfig() {
  if (_config) return _config;
  // `/api/config` é a única fonte de MAPS_API_KEY. NÃO há mais fallback pra
  // /data/credentials.json — esse arquivo foi removido por ser uma chave em
  // texto puro versionada no repo. Configure MAPS_API_KEY no env do
  // Cloudflare Pages (Production e Preview) como Secret.
  try {
    // authFetch anexa o token Firebase — /api/config exige usuário logado.
    const res = await authFetch('/api/config');
    if (res.ok) {
      const json = await res.json();
      if (json.mapsApiKey) {
        _config = { apiKey: json.mapsApiKey, mapId: json.mapsMapId || '' };
        return _config;
      }
      console.warn(
        'Google Maps desabilitado: MAPS_API_KEY ausente no env do Pages. ' +
        'Configure em Cloudflare Pages → Settings → Environment variables ' +
        '(tab Production e Preview) como Secret.',
      );
    }
  } catch (e) { console.warn('Google Maps: /api/config falhou:', e?.message || e); }
  _config = { apiKey: '', mapId: '' };
  return _config;
}

function _injectScript(apiKey) {
  return new Promise((resolve, reject) => {
    if (!apiKey) { reject(new Error('Missing Google Maps API key')); return; }
    const callbackName = '__gmapsLoaded_' + Math.random().toString(36).slice(2);
    globalThis[callbackName] = () => { delete globalThis[callbackName]; resolve(); };
    const script = document.createElement('script');
    const params = new URLSearchParams({
      key: apiKey,
      v: 'weekly',
      libraries: 'maps,marker,places',
      language: 'pt-BR',
      loading: 'async',
      callback: callbackName,
    });
    script.src = 'https://maps.googleapis.com/maps/api/js?' + params.toString();
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Google Maps script failed to load'));
    document.head.appendChild(script);
  });
}

export function loadGoogleMaps() {
  if (_bootstrapPromise) return _bootstrapPromise;
  _bootstrapPromise = (async () => {
    const cfg = await _fetchConfig();
    if (!cfg.apiKey) throw new Error('Google Maps API key not configured');
    await _injectScript(cfg.apiKey);
    return cfg;
  })();
  return _bootstrapPromise;
}

export async function getGoogleMapsConfig() {
  return _fetchConfig();
}

export async function getGoogleMapsLibrary(name) {
  await loadGoogleMaps();
  return google.maps.importLibrary(name);
}

// Back-compat: main.js calls loadPlacesSDK() after boot to warm the loader.
export function loadPlacesSDK() {
  loadGoogleMaps().catch(err => console.warn('Google Maps load failed:', err));
}

export function attachPlacesAutocomplete(inputId, onSelect) {
  loadGoogleMaps().then(() => {
    const input = document.getElementById(inputId);
    if (!input) return;
    const ac = new google.maps.places.Autocomplete(input, { language: 'pt-BR' });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      onSelect({ name: place.formatted_address || place.name || input.value, lat, lng });
    });
  }).catch(err => console.warn('Autocomplete disabled:', err));
}

// Variante avançada com 2 sinais distintos:
//  - `onPick(place)`  — user CLICOU numa sugestão do dropdown do Google
//    (place_changed dispara com geometry válida).
//  - `onSubmit(query)` — user apertou Enter SEM ter clicado em sugestão
//    (texto livre). Útil pra disparar text-search programático.
//
// Pra distinguir os dois cenários usamos um flag `_justPicked` que vira
// true no place_changed e é resetado no próximo input event. Quando Enter
// dispara, se `_justPicked` ainda é true a gente assume que o user
// confirmou a escolha do autocomplete (já tratada via onPick) e ignora o
// submit. Caso contrário, é texto puro → onSubmit.
//
// Retorna fn de cleanup (idempotente). Não rejeita se SDK falhar — só
// loga warning e deixa o input como text input puro.
export async function attachPlacesAutocompleteAdvanced(input, { onPick, onSubmit } = {}) {
  if (!input) return () => {};
  try {
    await loadGoogleMaps();
  } catch (err) {
    console.warn('[places] SDK failed to load — input fica como texto puro:', err);
    // Mesmo sem SDK, ainda conseguimos honrar onSubmit no Enter.
    if (onSubmit) {
      const fallbackKey = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const q = (input.value || '').trim();
          if (q) onSubmit(q);
        }
      };
      input.addEventListener('keydown', fallbackKey);
      return () => input.removeEventListener('keydown', fallbackKey);
    }
    return () => {};
  }

  const g = window.google;
  if (!g?.maps?.places?.Autocomplete) {
    console.warn('[places] google.maps.places.Autocomplete indisponível');
    return () => {};
  }

  const ac = new g.maps.places.Autocomplete(input, {
    fields: ['name', 'formatted_address', 'geometry', 'place_id'],
    language: 'pt-BR',
  });

  // Flag que indica que o último place_changed válido foi agora-agora.
  // Resetada em qualquer 'input' subsequente (= user digitou de novo).
  let _justPicked = false;

  const onInput = () => { _justPicked = false; };
  input.addEventListener('input', onInput);

  const pickedListener = ac.addListener('place_changed', () => {
    const p = ac.getPlace();
    if (!p?.geometry?.location) return;
    _justPicked = true;
    const place = {
      name: p.name || p.formatted_address || input.value,
      address: p.formatted_address || '',
      placeId: p.place_id || '',
      lat: p.geometry.location.lat(),
      lng: p.geometry.location.lng(),
    };
    onPick?.(place);
  });

  // Enter handler: se acabamos de pickar (place_changed disparou no mesmo
  // ciclo), o Google já tratou. Caso contrário, texto livre → onSubmit.
  // Usamos keydown (não keyup) pra preventDefault antes do form submit
  // padrão do <input type="search">.
  const onKeydown = (e) => {
    if (e.key !== 'Enter') return;
    // Microtask delay: se o user clicou numa sugestão do dropdown via Enter,
    // o place_changed do Google dispara ANTES desse keydown event handler
    // em alguns navegadores, mas em outros DEPOIS. Esperar um tick garante
    // que _justPicked já refletiu o estado.
    setTimeout(() => {
      if (_justPicked) return;
      const q = (input.value || '').trim();
      if (q) onSubmit?.(q);
    }, 0);
    // Evita submit do form (caso o input esteja dentro de <form>) e o
    // beep do navegador.
    e.preventDefault();
  };
  input.addEventListener('keydown', onKeydown);

  return () => {
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKeydown);
    if (pickedListener?.remove) pickedListener.remove();
    if (g.maps.event?.clearInstanceListeners) {
      g.maps.event.clearInstanceListeners(ac);
    }
  };
}

// Text-search programático (sem autocomplete): usado quando o user
// submete a busca digitando Enter sem ter clicado em sugestão. Devolve
// até `maxResults` lugares próximos ao `center` no shape padrão usado
// pelos suggestion cards (idêntico ao `searchNearbyPlaces` em
// mapPlaces.js — placeId/name/address/lat/lng/photoUrl/rating).
//
// Usa `Place.searchByText` (Places API New). Se não estiver disponível
// (key sem API New habilitada), retorna array vazio + warn — não vale
// implementar fallback legacy aqui por enquanto (caso raro; tela ainda
// funciona, só não mostra resultados de text search).
export async function searchPlacesByText(query, { center, radius = 5000, maxResults = 4 } = {}) {
  const q = (query || '').trim();
  if (!q) return [];
  // Cache (memória → KV) antes de tocar na Places API. Text Search é um dos
  // SKUs mais caros do Google Maps.
  const cacheKey = textCacheKey(q, center, maxResults);
  if (cacheKey) {
    const hit = await cacheGet(cacheKey);
    if (hit) return hit;
  }
  try {
    const { Place } = await getGoogleMapsLibrary('places');
    if (!Place || typeof Place.searchByText !== 'function') {
      console.warn('[places] Place.searchByText indisponível — API Places (New) precisa estar habilitada');
      return [];
    }
    const request = {
      textQuery: q,
      fields: ['id', 'displayName', 'formattedAddress', 'location', 'rating', 'userRatingCount', 'photos'],
      maxResultCount: Math.min(Math.max(1, maxResults), 20),
      language: 'pt-BR',
    };
    if (center && isFinite(Number(center.lat)) && isFinite(Number(center.lng))) {
      request.locationBias = {
        center: { lat: Number(center.lat), lng: Number(center.lng) },
        radius: Math.max(100, Math.min(50000, radius)),
      };
    }
    const response = await Place.searchByText(request);
    const places = response?.places || [];
    const result = places.map((p) => {
      const loc = p.location;
      return {
        placeId: p.id || '',
        name: typeof p.displayName === 'string' ? p.displayName : (p.displayName?.text || ''),
        address: p.formattedAddress || '',
        lat: typeof loc?.lat === 'function' ? loc.lat() : (loc?.lat ?? null),
        lng: typeof loc?.lng === 'function' ? loc.lng() : (loc?.lng ?? null),
        photoUrl: p.photos?.[0]
          ? p.photos[0].getURI({ maxWidth: 400, maxHeight: 400 })
          : null,
        rating: p.rating ?? null,
        userRatingCount: p.userRatingCount ?? null,
        type: 'search',
        openNow: null,
        todayHours: '',
      };
    }).filter((p) => isFinite(Number(p.lat)) && isFinite(Number(p.lng)));
    // Write-through: só cacheia resultado não-vazio.
    if (cacheKey && result.length) cacheSet(cacheKey, result);
    return result;
  } catch (err) {
    console.warn('[places] searchPlacesByText falhou:', err?.message || err);
    return [];
  }
}

// ── Resolução FRESCA de foto por placeId ────────────────────────────────────
// A URL de foto do Google (getURI) embute uma "referência" que EXPIRA depois
// de um tempo (dias). Por isso ela NÃO pode ser cacheada no KV de 120 dias —
// quando a referência vence, a imagem dá 403. Aqui resolvemos a foto na hora
// pelo placeId (Place Details, só o campo `photos`), com um cache de SESSÃO
// (memória, ~1h) pra não refazer a chamada a cada render. O placeId em si é
// estável e continua cacheado normalmente (via search/details).
const _photoMem = new Map();                  // `${id}@${w}x${h}` → { url, ts }
const PHOTO_MEM_TTL_MS = 60 * 60 * 1000;      // 1h

export async function resolvePlacePhotoUrl(placeId, { maxWidth = 400, maxHeight = 400 } = {}) {
  const id = String(placeId || '').trim();
  if (!id) return null;
  const k = `${id}@${maxWidth}x${maxHeight}`;
  const hit = _photoMem.get(k);
  if (hit && (Date.now() - hit.ts) < PHOTO_MEM_TTL_MS) return hit.url;
  try {
    const { Place } = await getGoogleMapsLibrary('places');
    if (!Place) return null;
    const place = new Place({ id });
    await place.fetchFields({ fields: ['photos'] });
    const url = place.photos?.[0]?.getURI
      ? place.photos[0].getURI({ maxWidth, maxHeight })
      : null;
    if (url) _photoMem.set(k, { url, ts: Date.now() });
    return url;
  } catch (err) {
    console.warn('[places] resolvePlacePhotoUrl falhou:', err?.message || err);
    return null;
  }
}

// Dado um ponto específico (hotel ou 1ª parada do itinerário), descobre a
// CIDADE dele e devolve o CENTRO da cidade — não o ponto original. Usado pra
// posicionar o pin do dia no centro da cidade (e não em cima do hotel/parada),
// resolvendo a sobreposição de marcadores. Reverse-geocode é SKU pago, então
// cacheia em memória + KV (chave por ~110 m). Best-effort: qualquer falha
// devolve null e o caller mantém o ponto original.
export async function geocodeCityCenter(refCoords) {
  const lat = Number(refCoords?.lat);
  const lng = Number(refCoords?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const cacheKey = geocodeCacheKey(lat, lng);
  if (cacheKey) {
    const hit = await cacheGet(cacheKey);
    if (hit && Number.isFinite(Number(hit.lat)) && Number.isFinite(Number(hit.lng))) return hit;
  }
  try {
    await loadGoogleMaps();
    if (!google?.maps?.Geocoder) return null;
    const geocoder = new google.maps.Geocoder();
    const resp = await geocoder.geocode({ location: { lat, lng }, language: 'pt-BR' });
    const center = pickCityCenter(resp?.results);
    if (center && cacheKey) cacheSet(cacheKey, center);
    return center;
  } catch (err) {
    console.warn('[places] geocodeCityCenter falhou:', err?.message || err);
    return null;
  }
}

// Geocoda um TEXTO (endereço/cidade) e devolve o CENTRO da cidade. Usado pra
// dias que não têm coords (ex: dia de volta sem hotel e com voos sem lat/lng):
// pega o melhor texto disponível, geocoda, e — se o resultado for um lugar
// específico em vez de uma cidade — faz reverse no ponto pra cair no centro da
// cidade. Cacheado (memória + KV) por query. Best-effort: falha → null.
export async function geocodeQueryToCityCenter(query) {
  const q = String(query || '').trim();
  if (!q) return null;
  const cacheKey = geocodeQueryCacheKey(q);
  if (cacheKey) {
    const hit = await cacheGet(cacheKey);
    if (hit && Number.isFinite(Number(hit.lat)) && Number.isFinite(Number(hit.lng))) return hit;
  }
  try {
    await loadGoogleMaps();
    if (!google?.maps?.Geocoder) return null;
    const geocoder = new google.maps.Geocoder();
    const resp = await geocoder.geocode({ address: q, language: 'pt-BR' });
    // 1) A query já é uma cidade? pega o nível-cidade direto.
    let center = pickCityCenter(resp?.results);
    // 2) Senão, é um lugar específico → usa o ponto e faz reverse pra cidade.
    if (!center) {
      const loc = resp?.results?.[0]?.geometry?.location;
      const lat = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
      const lng = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
      if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
        center = await geocodeCityCenter({ lat: Number(lat), lng: Number(lng) })
          || { lat: Number(lat), lng: Number(lng) };   // pior caso: o próprio ponto
      }
    }
    if (center && cacheKey) cacheSet(cacheKey, center);
    return center;
  } catch (err) {
    console.warn('[places] geocodeQueryToCityCenter falhou:', err?.message || err);
    return null;
  }
}

// Horário de hoje a partir de `regularOpeningHours`. weekdayDescriptions é
// ["segunda-feira: 09:00 – 18:00", ...] indexado Mon=0..Sun=6 pelo Google.
function _todayHoursFromOH(oh) {
  if (!oh?.weekdayDescriptions?.length) return '';
  const jsDay = new Date().getDay();   // Sun=0..Sat=6
  const gIdx = (jsDay + 6) % 7;        // Google: Mon=0..Sun=6
  const desc = oh.weekdayDescriptions[gIdx] || '';
  return desc.split(':').slice(1).join(':').trim();
}

// Detalhes de um lugar pelo placeId — usado pra ENRIQUECER um resultado de
// busca vindo do autocomplete (que só traz nome/endereço/coords) com
// foto/nota/horário. Sem isso o card de busca por autocomplete nunca tem
// foto. Best-effort: sempre resolve, nunca rejeita; campos ausentes voltam
// null/''. Shape compatível com os suggestion cards.
export async function fetchPlaceDetails(placeId) {
  const id = String(placeId || '').trim();
  if (!id) return null;
  // Cache (memória → KV): Place Details — o placeId é uma chave perfeita.
  const cacheKey = detailsCacheKey(id);
  if (cacheKey) {
    const hit = await cacheGet(cacheKey);
    if (hit) return hit;
  }
  try {
    const { Place } = await getGoogleMapsLibrary('places');
    if (!Place) return null;
    const place = new Place({ id });
    await place.fetchFields({
      fields: [
        'displayName', 'formattedAddress', 'location', 'photos',
        'rating', 'userRatingCount', 'regularOpeningHours',
        // utcOffsetMinutes: fuso do LUGAR. É o que permite interpretar o
        // horário digitado pelo usuário como hora local do destino (e não do
        // aparelho dele) ao pedir horários de transporte na Directions.
        // Vem no mesmo fetchFields, sem chamada nem SKU novo.
        'utcOffsetMinutes',
      ],
    });
    const loc = place.location;
    const photo = place.photos?.[0];
    const oh = place.regularOpeningHours;
    const details = {
      placeId: id,
      name: typeof place.displayName === 'string'
        ? place.displayName : (place.displayName?.text || ''),
      address: place.formattedAddress || '',
      lat: loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null,
      lng: loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null,
      photoUrl: photo?.getURI ? photo.getURI({ maxWidth: 400, maxHeight: 400 }) : null,
      rating: place.rating ?? null,
      userRatingCount: place.userRatingCount ?? null,
      openNow: oh?.openNow ?? null,
      todayHours: _todayHoursFromOH(oh),
      // Offset do lugar em minutos (ex: Zurique no verão → 120). null quando
      // o Google não devolve.
      utcOffsetMinutes: Number.isFinite(Number(place.utcOffsetMinutes))
        ? Number(place.utcOffsetMinutes) : null,
    };
    if (cacheKey) cacheSet(cacheKey, details);
    return details;
  } catch (err) {
    console.warn('[places] fetchPlaceDetails falhou:', err?.message || err);
    return null;
  }
}
